import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AnimatePresence,
  motion,
} from "framer-motion";
import {
  IoRefreshOutline,
  IoTicketOutline,
} from "react-icons/io5";

import axios from "axios";

import { LuHandMetal } from "react-icons/lu";

import FirstGatheringRecommendation from "./FirstGatheringRecommendation";

import { ReactComponent as TribeLogo } from "../assets/TribeLogo.svg";

const INTRO_STORAGE_KEY =
  "tribeFirstReservationIntroSeen";

const RESERVATION_VIDEO_URL =
  "https://res.cloudinary.com/dy7dovjqb/video/upload/v1784150794/sdfsdf_bn6uil.mp4";


const INTRO_COLLAPSE_DELAY_MS = 4100;
const REDUCED_MOTION_COLLAPSE_DELAY_MS = 700;
const SUCCESS_VISIBLE_MS = 20000;
const SUCCESS_SEEN_THRESHOLD_MS = 6000;
const SUCCESS_EXIT_MS = 260;
const SUCCESS_STORAGE_PREFIX =
  "tribeReservationSuccessSeen";
const FINAL_FRAME_EPSILON_SECONDS = 0.04;
const SOCIAL_PROOF_AVATAR_COUNT = 5;
const SOCIAL_PROOF_PRELOAD_LIMIT = 18;
const PENDING_RESERVATION_SUCCESS_KEY =
  "tribePendingReservationSuccess";

/**
 * Keep true while testing to ignore localStorage and replay after refresh.
 * This does not bypass the new-user eligibility check.
 * Change to false before production.
 */
const TEST_ALWAYS_SHOW_INTRO = false;

function hasStoredIntroCompletion() {
  return Boolean(
    !TEST_ALWAYS_SHOW_INTRO &&
      typeof window !== "undefined" &&
      window.localStorage.getItem(
        INTRO_STORAGE_KEY
      ) === "true"
  );
}

function getReservationSuccessStorageKey(
  userId,
  gatheringId
) {
  const safeUserId =
    String(userId || "anonymous").trim() ||
    "anonymous";

  const safeGatheringId = String(
    gatheringId || ""
  ).trim();

  if (!safeGatheringId) {
    return "";
  }

  return `${SUCCESS_STORAGE_PREFIX}:${safeUserId}:${safeGatheringId}`;
}

function hasStoredReservationSuccess(
  userId,
  gatheringId
) {
  if (typeof window === "undefined") {
    return false;
  }

  const storageKey =
    getReservationSuccessStorageKey(
      userId,
      gatheringId
    );

  if (!storageKey) {
    return false;
  }

  return (
    window.localStorage.getItem(
      storageKey
    ) === "true"
  );
}

function storeReservationSuccessSeen(
  userId,
  gatheringId
) {
  if (typeof window === "undefined") {
    return;
  }

  const storageKey =
    getReservationSuccessStorageKey(
      userId,
      gatheringId
    );

  if (!storageKey) {
    return;
  }

  window.localStorage.setItem(
    storageKey,
    "true"
  );
}

const TEXT_STEPS = [
  {
    id: "people",
    prefix: "You joined Tribe to meet ",
    animated: "people.",
  },
  {
    id: "your-people",
    prefix: "You joined Tribe to meet ",
    animated: "your people.",
  },
  {
    id: "one-gathering",
    prefix: "It starts with ",
    animated: "one gathering.",
  },
];

function getProfilePhoto(person) {
  return String(
    person?.profilePicUrl ||
      person?.profilePictureUrl ||
      person?.avatarUrl ||
      person?.photoUrl ||
      person?.imageUrl ||
      ""
  ).trim();
}

function getPersonId(person, photoUrl) {
  return String(
    person?._id ||
      person?.id ||
      person?.userId ||
      photoUrl
  );
}

function imageLoads(photoUrl) {
  if (
    typeof window === "undefined" ||
    typeof window.Image !== "function"
  ) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const image = new window.Image();

    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    image.src = photoUrl;
  });
}

function useRandomSocialProofPeople(enabled) {
  const [socialProof, setSocialProof] =
    useState({
      people: [],
      totalPeople: 0,
    });

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    async function loadPeople() {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/social-proof`,
          {
            signal: controller.signal,
            params: {
              limit:
                SOCIAL_PROOF_PRELOAD_LIMIT,
              excludeUserId:
                localStorage.getItem(
                  "userId"
                ) || undefined,
            },
          }
        );

        const users = Array.isArray(
          data?.users
        )
          ? data.users
          : [];

        const candidates = users
          .map((person) => {
            const photoUrl =
              getProfilePhoto(person);

            if (!photoUrl) {
              return null;
            }

            return {
              id: getPersonId(
                person,
                photoUrl
              ),
              photoUrl,
            };
          })
          .filter(Boolean);

        const validatedCandidates =
          await Promise.all(
            candidates.map(
              async (person) => ({
                ...person,
                imageLoaded:
                  await imageLoads(
                    person.photoUrl
                  ),
              })
            )
          );

        if (!active) {
          return;
        }

        const people =
          validatedCandidates
            .filter(
              (person) =>
                person.imageLoaded
            )
            .slice(
              0,
              SOCIAL_PROOF_AVATAR_COUNT
            )
            .map(
              ({
                imageLoaded,
                ...person
              }) => person
            );

        const reportedTotal = Number(
          data?.totalPeople
        );

        setSocialProof({
          people,
          totalPeople:
            Number.isFinite(
              reportedTotal
            ) && reportedTotal > 0
              ? reportedTotal
              : people.length,
        });
      } catch (error) {
        if (
          error?.code === "ERR_CANCELED" ||
          error?.name === "CanceledError"
        ) {
          return;
        }

        console.warn(
          "[FirstReservationIntro] Could not load social proof:",
          error?.response?.data ||
            error?.message ||
            error
        );
      }
    }

    loadPeople();

    return () => {
      active = false;
      controller.abort();
    };
  }, [enabled]);

  return socialProof;
}

function useReducedMotionPreference() {
  const [reducedMotion, setReducedMotion] =
    useState(false);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !window.matchMedia
    ) {
      return undefined;
    }

    const media = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );

    const updatePreference = () => {
      setReducedMotion(media.matches);
    };

    updatePreference();

    media.addEventListener?.(
      "change",
      updatePreference
    );

    return () => {
      media.removeEventListener?.(
        "change",
        updatePreference
      );
    };
  }, []);

  return reducedMotion;
}

function TypewriterText({
  text,
  deleting = false,
  speed = 42,
  deleteSpeed = 24,
  onComplete,
}) {
  const [visibleLength, setVisibleLength] =
    useState(deleting ? text.length : 0);

  useEffect(() => {
    setVisibleLength(
      deleting ? text.length : 0
    );
  }, [text, deleting]);

  useEffect(() => {
    const finished = deleting
      ? visibleLength <= 0
      : visibleLength >= text.length;

    if (finished) {
      onComplete?.();
      return undefined;
    }

    const timer = window.setTimeout(
      () => {
        setVisibleLength((current) =>
          deleting
            ? Math.max(0, current - 1)
            : Math.min(
                text.length,
                current + 1
              )
        );
      },
      deleting ? deleteSpeed : speed
    );

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    visibleLength,
    text,
    deleting,
    speed,
    deleteSpeed,
    onComplete,
  ]);

  return (
    <span>
      {text.slice(0, visibleLength)}

      <span style={styles.cursor}>|</span>
    </span>
  );
}

function ReservationVideo({
  collapsed,
  shouldPlay,
  reducedMotion,
  onPlaybackComplete,
}) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return undefined;
    }

    video.muted = true;
    video.pause();

    const showFinalFrame = () => {
      video.pause();

      if (
        Number.isFinite(video.duration) &&
        video.duration > 0
      ) {
        video.currentTime = Math.max(
          0,
          video.duration -
            FINAL_FRAME_EPSILON_SECONDS
        );
      }
    };

    const playFromBeginning = () => {
      video.currentTime = 0;

      const playPromise = video.play();

      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    };

    const handleEnded = () => {
      showFinalFrame();
      onPlaybackComplete?.();
    };

    video.addEventListener(
      "ended",
      handleEnded
    );

    if (collapsed) {
      if (video.readyState >= 1) {
        showFinalFrame();
      } else {
        video.addEventListener(
          "loadedmetadata",
          showFinalFrame,
          { once: true }
        );
      }

      return () => {
        video.removeEventListener(
          "ended",
          handleEnded
        );

        video.removeEventListener(
          "loadedmetadata",
          showFinalFrame
        );
      };
    }

    if (!shouldPlay || reducedMotion) {
      if (video.readyState >= 1) {
        video.currentTime = 0;
      }

      return () => {
        video.removeEventListener(
          "ended",
          handleEnded
        );
      };
    }

    if (video.readyState >= 2) {
      playFromBeginning();
    } else {
      video.addEventListener(
        "loadeddata",
        playFromBeginning,
        { once: true }
      );
    }

    return () => {
      video.removeEventListener(
        "ended",
        handleEnded
      );

      video.removeEventListener(
        "loadeddata",
        playFromBeginning
      );

      video.pause();
    };
  }, [
    collapsed,
    shouldPlay,
    reducedMotion,
    onPlaybackComplete,
  ]);

  return (
    <video
      ref={videoRef}
      src={RESERVATION_VIDEO_URL}
      muted
      playsInline
      preload="auto"
      disablePictureInPicture
      aria-hidden="true"
      style={{
        ...styles.reservationVideo,
        ...(collapsed
          ? styles.reservationVideoCollapsed
          : styles.reservationVideoExpanded),
      }}
    />
  );
}

function AnimatedReservationOrb({
  collapsed,
  shouldPlay,
  reducedMotion,
  onPlaybackComplete,
}) {
  return (
    <motion.div
      layout
      layoutId="first-reservation-orb"
      initial={false}
      animate={{
        scale: 1,
      }}
      transition={{
        layout: {
          type: "spring",
          stiffness: 185,
          damping: 24,
          mass: 0.86,
        },
      }}
      style={{
        ...styles.orb,
        ...(collapsed
          ? styles.orbCollapsed
          : styles.orbExpanded),
      }}
    >
      <ReservationVideo
        collapsed={collapsed}
        shouldPlay={shouldPlay}
        reducedMotion={reducedMotion}
        onPlaybackComplete={
          onPlaybackComplete
        }
      />
    </motion.div>
  );
}


function getMemberFirstName(
  membership,
  providedFirstName
) {
  let storedProfile = null;

  if (typeof window !== "undefined") {
    const storedProfileValue =
      window.localStorage.getItem("user") ||
      window.localStorage.getItem(
        "currentUser"
      ) ||
      window.localStorage.getItem("profile");

    if (storedProfileValue) {
      try {
        storedProfile = JSON.parse(
          storedProfileValue
        );
      } catch {
        storedProfile = null;
      }
    }
  }

  const storedName =
    typeof window !== "undefined"
      ? window.localStorage.getItem(
          "firstName"
        ) ||
        window.localStorage.getItem(
          "userFirstName"
        ) ||
        window.localStorage.getItem("name") ||
        window.localStorage.getItem(
          "fullName"
        ) ||
        window.localStorage.getItem(
          "userName"
        )
      : "";

  const fullName = String(
    providedFirstName ||
      membership?.firstName ||
      membership?.user?.firstName ||
      membership?.member?.firstName ||
      membership?.profile?.firstName ||
      membership?.name ||
      membership?.user?.name ||
      storedProfile?.firstName ||
      storedProfile?.name ||
      storedName ||
      ""
  ).trim();

  return fullName.split(/\s+/)[0] || "";
}

export default function FirstReservationIntro({
  membership,
  onExplore,
  onReserveRecommended,
  onBlockingChange,
  recommendedGathering = null,
  userFirstName = "",
  hasUpcomingReservation = null,
  currentGatheringId = null,
  forceVisible = false,
}) {
  const reducedMotion =
    useReducedMotionPreference();

  const initialSeen =
    hasStoredIntroCompletion();

  const [collapsed, setCollapsed] =
    useState(initialSeen);

  const [
    showRecommendation,
    setShowRecommendation,
  ] = useState(false);

const recommendationState = {
  gathering: recommendedGathering || null,
  loading: false,
};

  const [
    showReservationSuccess,
    setShowReservationSuccess,
  ] = useState(false);

  const [
    successDismissing,
    setSuccessDismissing,
  ] = useState(false);

  const [sequenceIndex, setSequenceIndex] =
    useState(
      reducedMotion || initialSeen
        ? TEXT_STEPS.length - 1
        : 0
    );

  const [deleting, setDeleting] =
    useState(false);

  const [
    sequenceComplete,
    setSequenceComplete,
  ] = useState(
    reducedMotion || initialSeen
  );

  const [videoComplete, setVideoComplete] =
    useState(
      reducedMotion || initialSeen
    );

  const [
    collapseDelayElapsed,
    setCollapseDelayElapsed,
  ] = useState(false);

  const introCollapseDelayMs =
    reducedMotion
      ? REDUCED_MOTION_COLLAPSE_DELAY_MS
      : INTRO_COLLAPSE_DELAY_MS;

  const timersRef = useRef([]);

  const membershipStatus = String(
    membership?.membershipStatus || ""
  )
    .trim()
    .toLowerCase();

  const normalizedCurrentUserId = String(
    (typeof window !== "undefined"
      ? window.localStorage.getItem(
          "userId"
        )
      : "") ||
      membership?.userId ||
      membership?.memberId ||
      membership?._id ||
      "anonymous"
  ).trim();

const pendingReservationSuccessId =
  typeof window !== "undefined"
    ? String(
        window.localStorage.getItem(
          PENDING_RESERVATION_SUCCESS_KEY
        ) || ""
      ).trim()
    : "";

const normalizedCurrentGatheringId =
  String(
    pendingReservationSuccessId ||
      currentGatheringId ||
      membership?.currentGatheringId ||
      membership?.upcomingGatheringId ||
      membership?.currentReservationEventId ||
      membership?.upcomingReservationEventId ||
      membership?.latestReservationEventId ||
      membership?.firstGatheringId ||
      membership?.upcomingReservation
        ?.event?._id ||
      membership?.upcomingReservation
        ?.eventId ||
      membership?.latestReservation
        ?.event?._id ||
      membership?.latestReservation
        ?.eventId ||
      ""
  ).trim();
  const hasEverAttendedGathering =
    membership
      ?.hasEverAttendedGathering ===
    true;

  const hasCurrentReservation =
    Boolean(
      membership
        ?.hasUpcomingReservation ===
        true ||
        hasUpcomingReservation ===
          true
    );


  const isActive = Boolean(
    membership?.membershipActive ===
      true &&
      ["active", "trialing"].includes(
        membershipStatus
      )
  );

  const hasCardIssue = Boolean(
    membership
      ?.membershipCardRequiresReplacement ===
      true ||
      membershipStatus === "card_issue" ||
      membershipStatus === "past_due" ||
      membershipStatus === "unpaid"
  );

  const meetsIntroRequirements =
    Boolean(
      forceVisible ||
        (membership &&
          isActive &&
          membership
            ?.membershipCardRequired !==
            true &&
          !hasCardIssue)
    );

  const isEligibleForIntro = Boolean(
    meetsIntroRequirements &&
      !hasEverAttendedGathering &&
      !hasCurrentReservation
  );

  const shouldShow = Boolean(
    !hasEverAttendedGathering &&
      (
        isEligibleForIntro ||
        showReservationSuccess
      )
  );

  const blocksLocationModal = Boolean(
  shouldShow &&
  !collapsed
);

useEffect(() => {
  onBlockingChange?.(
    blocksLocationModal
  );

  return () => {
    onBlockingChange?.(false);
  };
}, [
  blocksLocationModal,
  onBlockingChange,
]);


  const previousEligibilityRef = useRef(
    isEligibleForIntro
  );

  const {
    people: socialProofPeople,
    totalPeople: socialProofPeopleCount,
  } = useRandomSocialProofPeople(
    isEligibleForIntro
  );

  const videoShouldPlay = Boolean(
    !collapsed &&
      !showRecommendation &&
      !reducedMotion &&
      sequenceIndex ===
        TEXT_STEPS.length - 1 &&
      !deleting
  );

  const currentStep =
    TEXT_STEPS[sequenceIndex] ||
    TEXT_STEPS[
      TEXT_STEPS.length - 1
    ];


  const selectedGathering =
    recommendationState.gathering;

  const memberFirstName =
    getMemberFirstName(
      membership,
      userFirstName
    );


  const clearTimers = useCallback(() => {
    timersRef.current.forEach((timer) => {
      window.clearTimeout(timer);
    });

    timersRef.current = [];
  }, []);

  const addTimer = useCallback(
    (callback, delay) => {
      const timer = window.setTimeout(
        callback,
        delay
      );

      timersRef.current.push(timer);

      return timer;
    },
    []
  );

useEffect(() => {
  if (
    !pendingReservationSuccessId ||
    !normalizedCurrentGatheringId
  ) {
    setShowReservationSuccess(false);
    setSuccessDismissing(false);
    return;
  }

  const successWasSeen =
    hasStoredReservationSuccess(
      normalizedCurrentUserId,
      normalizedCurrentGatheringId
    );

  if (successWasSeen) {
    window.localStorage.removeItem(
      PENDING_RESERVATION_SUCCESS_KEY
    );

    setShowReservationSuccess(false);
    setSuccessDismissing(false);
    return;
  }

  clearTimers();
  setSuccessDismissing(false);
  setShowReservationSuccess(true);
  setCollapsed(true);
}, [
  pendingReservationSuccessId,
  normalizedCurrentUserId,
  normalizedCurrentGatheringId,
  clearTimers,
]);

useEffect(() => {
  if (!showReservationSuccess) {
    return undefined;
  }

 const seenTimer =
  window.setTimeout(() => {
    if (!normalizedCurrentGatheringId) {
      return;
    }

    storeReservationSuccessSeen(
      normalizedCurrentUserId,
      normalizedCurrentGatheringId
    );

    const pendingId = String(
      window.localStorage.getItem(
        PENDING_RESERVATION_SUCCESS_KEY
      ) || ""
    ).trim();

    if (
      pendingId === normalizedCurrentGatheringId
    ) {
      window.localStorage.removeItem(
        PENDING_RESERVATION_SUCCESS_KEY
      );
    }
  }, SUCCESS_SEEN_THRESHOLD_MS);

  const dismissTimer =
    window.setTimeout(() => {
      setSuccessDismissing(true);
    }, SUCCESS_VISIBLE_MS);

  const removeTimer =
    window.setTimeout(() => {
      window.localStorage.removeItem(
        PENDING_RESERVATION_SUCCESS_KEY
      );

      setShowReservationSuccess(false);
      setSuccessDismissing(false);
    }, SUCCESS_VISIBLE_MS + SUCCESS_EXIT_MS);

  return () => {
    window.clearTimeout(seenTimer);
    window.clearTimeout(dismissTimer);
    window.clearTimeout(removeTimer);
  };
}, [
  showReservationSuccess,
  normalizedCurrentUserId,
  normalizedCurrentGatheringId,
]);

  /**
   * Reserving hides the component immediately. Canceling makes it
   * eligible again. Attended and no-show outcomes keep it hidden.
   * A completed intro returns as the collapsed banner; an interrupted
   * intro restarts from the beginning.
   */
  useEffect(() => {
    const wasEligible =
      previousEligibilityRef.current;

    previousEligibilityRef.current =
      isEligibleForIntro;

    if (!isEligibleForIntro) {
      clearTimers();
      return;
    }

    if (wasEligible) {
      return;
    }

    const introWasCompleted =
      hasStoredIntroCompletion();

    setShowReservationSuccess(false);
    setShowRecommendation(false);
    setCollapsed(introWasCompleted);
    setDeleting(false);
    setSequenceIndex(
      introWasCompleted ||
        reducedMotion
        ? TEXT_STEPS.length - 1
        : 0
    );
    setSequenceComplete(
      introWasCompleted ||
        reducedMotion
    );
    setVideoComplete(
      introWasCompleted ||
        reducedMotion
    );
    setCollapseDelayElapsed(false);
  }, [
    isEligibleForIntro,
    reducedMotion,
    clearTimers,
  ]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  useEffect(() => {
    if (
      !shouldShow ||
      collapsed ||
      showReservationSuccess ||
      reducedMotion
    ) {
      return undefined;
    }

    document.body.style.overflow =
      "hidden";
    document.documentElement.style.overflow =
      "hidden";

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow =
        "";
    };
  }, [
    shouldShow,
    collapsed,
    showReservationSuccess,
    reducedMotion,
  ]);

  useEffect(() => {
    if (!shouldShow) {
      document.body.style.overflow = "";
      document.documentElement.style.overflow =
        "";
    }
  }, [shouldShow]);

  const revealRecommendation =
    useCallback(() => {
      if (
        !sequenceComplete ||
        !videoComplete ||
        !collapseDelayElapsed
      ) {
        return;
      }

      clearTimers();

      if (!TEST_ALWAYS_SHOW_INTRO) {
        window.localStorage.setItem(
          INTRO_STORAGE_KEY,
          "true"
        );
      }

      setShowRecommendation(true);
      setSequenceComplete(true);
      setDeleting(false);
    }, [
      clearTimers,
      sequenceComplete,
      videoComplete,
      collapseDelayElapsed,
    ]);

  const handleSkipToRecommendation =
    useCallback(() => {
      clearTimers();

      if (!TEST_ALWAYS_SHOW_INTRO) {
        window.localStorage.setItem(
          INTRO_STORAGE_KEY,
          "true"
        );
      }

      setSequenceIndex(
        TEXT_STEPS.length - 1
      );
      setDeleting(false);
      setSequenceComplete(true);
      setVideoComplete(true);
      setCollapseDelayElapsed(true);
      setCollapsed(false);
      setShowRecommendation(true);
    }, [clearTimers]);

  const handleVideoComplete =
    useCallback(() => {
      setVideoComplete(true);
    }, []);

  const handleReplayIntro = () => {
    clearTimers();

    setDeleting(false);
    setSequenceIndex(
      reducedMotion
        ? TEXT_STEPS.length - 1
        : 0
    );
    setSequenceComplete(reducedMotion);
    setVideoComplete(reducedMotion);
    setCollapseDelayElapsed(false);
    setShowRecommendation(false);
    setCollapsed(false);
  };


  const handleReserveRecommendation = () => {
    if (!selectedGathering) {
      return;
    }

    setShowRecommendation(false);
    setCollapsed(true);

    if (
      typeof onReserveRecommended ===
      "function"
    ) {
      onReserveRecommended(
        selectedGathering
      );
      return;
    }

    onExplore?.(selectedGathering);
  };

  const handleViewAllEvents = () => {
    setShowRecommendation(false);
    setCollapsed(true);
    onExplore?.();
  };

  useEffect(() => {
    if (
      !shouldShow ||
      collapsed ||
      showRecommendation ||
      !sequenceComplete
    ) {
      return undefined;
    }

    const timer = addTimer(
      () =>
        setCollapseDelayElapsed(true),
      introCollapseDelayMs
    );

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    shouldShow,
    collapsed,
    showRecommendation,
    sequenceComplete,
    introCollapseDelayMs,
    addTimer,
  ]);

  useEffect(() => {
    if (
      !shouldShow ||
      collapsed ||
      showRecommendation ||
      !sequenceComplete ||
      !videoComplete ||
      !collapseDelayElapsed
    ) {
      return;
    }

    revealRecommendation();
  }, [
    shouldShow,
    collapsed,
    showRecommendation,
    sequenceComplete,
    videoComplete,
    collapseDelayElapsed,
    revealRecommendation,
  ]);

  const handleTypedComplete =
    useCallback(() => {
      if (
        reducedMotion ||
        collapsed ||
        sequenceComplete
      ) {
        return;
      }

      clearTimers();

      if (
        sequenceIndex === 0 &&
        !deleting
      ) {
        addTimer(() => {
          setDeleting(true);
        }, 800);

        return;
      }

      if (
        sequenceIndex === 0 &&
        deleting
      ) {
        setDeleting(false);
        setSequenceIndex(1);
        return;
      }

      if (sequenceIndex === 1) {
        addTimer(() => {
          setSequenceIndex(2);
        }, 950);

        return;
      }

      if (
        sequenceIndex ===
        TEXT_STEPS.length - 1
      ) {
        addTimer(() => {
          setSequenceComplete(true);
        }, 350);
      }
    }, [
      reducedMotion,
      collapsed,
      sequenceComplete,
      clearTimers,
      sequenceIndex,
      deleting,
      addTimer,
    ]);

  const fullTitle = useMemo(
    () => (
      <>
        <span
          style={styles.headlinePrefix}
        >
          {currentStep.prefix}
        </span>

        <span
          style={{
            ...styles.headlineEmphasis,
            ...(currentStep.id ===
            "your-people"
              ? styles.highlightedWords
              : {}),
          }}
        >
          {reducedMotion ? (
            currentStep.animated
          ) : (
            <TypewriterText
              key={`${currentStep.id}-${deleting}`}
              text={currentStep.animated}
              deleting={deleting}
              onComplete={
                handleTypedComplete
              }
            />
          )}
        </span>
      </>
    ),
    [
      currentStep,
      deleting,
      reducedMotion,
      handleTypedComplete,
    ]
  );

  if (!shouldShow) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.section
        layout
        initial={false}
        animate={
          showReservationSuccess
            ? {
                top: "auto",
                left: 12,
                right: 12,
                bottom:
                  "calc(12px + env(safe-area-inset-bottom, 0px))",
                height: 102,
                borderRadius: 12,
                boxShadow:
                  "0 -10px 32px rgba(15,23,42,0.16)",
                opacity:
                  successDismissing
                    ? 0
                    : 1,
                y: successDismissing
                  ? 14
                  : 0,
              }
            : collapsed
              ? {
                  top: "auto",
                  left: 12,
                  right: 12,
                  bottom:
                    "calc(12px + env(safe-area-inset-bottom, 0px))",
                  height: 74,
                  borderRadius: 12,
                  boxShadow:
                    "0 -10px 32px rgba(15,23,42,0.16)",
                }
              : {
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: "100dvh",
                  borderRadius: 0,
                  boxShadow:
                    "0 0 0 rgba(15,23,42,0)",
                }
        }
        transition={
          reducedMotion
            ? { duration: 0 }
            : successDismissing
              ? {
                  duration:
                    SUCCESS_EXIT_MS /
                    1000,
                  ease: [
                    0.22,
                    1,
                    0.36,
                    1,
                  ],
                }
              : {
                  type: "spring",
                  stiffness: 165,
                  damping: 25,
                  mass: 0.92,
                }
        }
        style={{
          ...styles.shell,
          ...(showReservationSuccess
            ? styles.shellSuccess
            : collapsed
              ? styles.shellCollapsed
              : styles.shellExpanded),
        }}
        aria-label={
          showReservationSuccess
            ? "Your first gathering is booked"
            : collapsed
              ? "Book your first gathering"
              : showRecommendation
                ? "Your recommended gathering"
                : "Welcome to Tribe"
        }
      >
     <AnimatePresence>
  {!collapsed &&
    !showReservationSuccess &&
    !showRecommendation && (
      <motion.div
        key="tribe-logo"
        initial={
          reducedMotion
            ? false
            : {
                opacity: 0,
                y: -8,
              }
        }
        animate={{
          opacity: 1,
          y: 0,
        }}
        exit={{
          opacity: 0,
          y: -8,
        }}
        transition={{
          duration: 0.35,
          ease: [0.22, 1, 0.36, 1],
        }}
        style={styles.topLogo}
      >
        <TribeLogo
          style={styles.topLogoSvg}
        />
      </motion.div>
    )}
</AnimatePresence>
        <motion.div
          layout
          style={{
            ...styles.content,
            ...(showReservationSuccess
              ? styles.contentSuccess
              : collapsed
                ? styles.contentCollapsed
                : showRecommendation
                  ? styles.contentRecommendation
                  : styles.contentExpanded),
          }}
        >
          {showReservationSuccess ? (
            <motion.div
              aria-hidden="true"
              initial={
                reducedMotion
                  ? false
                  : {
                      opacity: 0,
                      scale: 0.72,
                    }
              }
              animate={{
                opacity: 1,
                scale: 1,
              }}
              transition={{
                type: "spring",
                stiffness: 420,
                damping: 24,
                delay: reducedMotion
                  ? 0
                  : 0.08,
              }}
              style={
                styles.successIconWrap
              }
            >
              <LuHandMetal
                size={28}
                strokeWidth={2}
              />
            </motion.div>
          ) : collapsed ? (
            <div
              aria-hidden="true"
              style={
                styles.ticketIconWrap
              }
            >
              <IoTicketOutline size={28} />
            </div>
          ) : (
            <AnimatePresence
              mode="wait"
              initial={false}
            >
              {showRecommendation ? null : (
                <motion.div
                  key="reservation-intro-media"
                  initial={false}
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                  }}
                  exit={
                    reducedMotion
                      ? undefined
                      : {
                          opacity: 0,
                          y: -12,
                          scale: 0.97,
                        }
                  }
                  transition={{
                    duration: 0.28,
                    ease: [
                      0.22,
                      1,
                      0.36,
                      1,
                    ],
                  }}
                >
                  <AnimatedReservationOrb
                    collapsed={false}
                    shouldPlay={
                      videoShouldPlay
                    }
                    reducedMotion={
                      reducedMotion
                    }
                    onPlaybackComplete={
                      handleVideoComplete
                    }
                  />
                </motion.div>
              )}
            </AnimatePresence>
          )}

          <AnimatePresence mode="wait">
            {showReservationSuccess ? (
              <motion.div
                key="reservation-success-copy"
                initial={
                  reducedMotion
                    ? false
                    : {
                        opacity: 0,
                        y: 7,
                      }
                }
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -5,
                }}
                transition={{
                  duration: 0.22,
                }}
                style={styles.successCopy}
              >
                <div
                  style={
                    styles.successTitle
                  }
                >
                  Your first gathering is{" "}
                  <span
                    style={
                      styles.successHighlight
                    }
                  >
                    <motion.span
                      aria-hidden="true"
                      initial={{
                        scaleX: 0,
                        rotate: -1.2,
                      }}
                      animate={{
                        scaleX: 1,
                        rotate: -1.2,
                      }}
                      transition={{
                        delay:
                          reducedMotion
                            ? 0
                            : 0.3,
                        duration:
                          reducedMotion
                            ? 0
                            : 0.5,
                        ease: [
                          0.22,
                          1,
                          0.36,
                          1,
                        ],
                      }}
                    />

                    <span>
                      booked
                    </span>
                  </span>
                </div>

     <div style={styles.successSubtitle}>
  Find it anytime on your{" "}
  <span style={styles.successInstructionStrong}>
    Reservations page
  </span>
  . When it’s time, tap{" "}
  <span style={styles.successInstructionStrong}>
    Enter portal
  </span>
  .
</div>
                <div
                  aria-hidden="true"
                  style={
                    styles.successCountdownTrack
                  }
                >
                  <motion.div
                    initial={{
                      scaleX: 1,
                    }}
                    animate={{
                      scaleX: 0,
                    }}
                    transition={{
                      duration:
                        SUCCESS_VISIBLE_MS /
                        1000,
                      ease: "linear",
                    }}
                    style={
                      styles.successCountdownFill
                    }
                  />
                </div>
              </motion.div>
            ) : collapsed ? (
              <motion.div
                key="compact-copy"
                initial={
                  reducedMotion
                    ? false
                    : {
                        opacity: 0,
                        y: 7,
                      }
                }
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -5,
                }}
                transition={{
                  duration: 0.22,
                }}
                style={styles.compactCopy}
              >
                <div
                  style={
                    styles.compactTitle
                  }
                >
                  Book your first gathering
                </div>

                <div
                  style={
                    styles.compactSubtitle
                  }
                >
                  Find one you like and
                  reserve your spot.
                </div>
              </motion.div>
            ) : showRecommendation ? (
      <motion.div
  key="recommendation-copy"
  initial={
    reducedMotion
      ? false
      : {
          opacity: 0,
          y: 16,
        }
  }
  animate={{
    opacity: 1,
    y: 0,
  }}
  exit={{
    opacity: 0,
    y: -10,
  }}
  transition={{
    duration: 0.44,
    ease: [0.22, 1, 0.36, 1],
  }}
  style={styles.recommendationCopy}
>
  <div style={styles.recommendationLogoWrap}>
    <TribeLogo
      style={styles.recommendationLogo}
    />
  </div>

  <FirstGatheringRecommendation
  gathering={selectedGathering}
  firstName={memberFirstName}
  loading={recommendationState.loading}
  onViewGathering={handleReserveRecommendation}
  onViewAll={handleViewAllEvents}
/>
</motion.div>
            ) : (
              <motion.div
                key="expanded-copy"
                initial={{
                  opacity: 0,
                  y: 14,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -10,
                }}
                transition={{
                  duration: 0.42,
                  ease: [
                    0.22,
                    1,
                    0.36,
                    1,
                  ],
                }}
                style={styles.expandedCopy}
              >
                <div
                  style={styles.eyebrow}
                >
                  YOUR FIRST GATHERING
                </div>

                <h1
                  style={styles.headline}
                >
                  {fullTitle}
                </h1>

                {!sequenceComplete && (
                  <button
                    type="button"
                    onClick={
                      handleSkipToRecommendation
                    }
                    style={styles.introSkipButton}
                  >
Skip intro
                  </button>
                )}

                <AnimatePresence>
                  {sequenceComplete && (
                    <motion.div
                      initial={
                        reducedMotion
                          ? false
                          : {
                              opacity: 0,
                              y: 12,
                            }
                      }
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      transition={{
                        duration: 0.4,
                        ease: [
                          0.22,
                          1,
                          0.36,
                          1,
                        ],
                      }}
                    >
                      <p
                        style={
                          styles.supportingText
                        }
                      >
                        Find a gathering you
                        like,{" "}
                        <span
                          style={
                            styles.supportingTextStrong
                          }
                        >
                          reserve your spot,
                        </span>{" "}
                        and take the first
                        step toward{" "}
                        <span
                          style={
                            styles.supportingTextStrong
                          }
                        >
                          finding your people.
                        </span>
                      </p>

                      {socialProofPeople.length >
                        0 && (
                        <div
                          style={
                            styles.socialProofRow
                          }
                        >
                          <div
                            style={
                              styles.avatarStack
                            }
                            aria-hidden="true"
                          >
                            {socialProofPeople.map(
                              (
                                person,
                                index
                              ) => (
                                <motion.img
                                  key={
                                    person.id
                                  }
                                  src={
                                    person.photoUrl
                                  }
                                  alt=""
                                  initial={
                                    reducedMotion
                                      ? false
                                      : {
                                          opacity:
                                            0,
                                          scale:
                                            0.72,
                                          x: -5,
                                        }
                                  }
                                  animate={{
                                    opacity: 1,
                                    scale: 1,
                                    x: 0,
                                  }}
                                  transition={{
                                    delay:
                                      reducedMotion
                                        ? 0
                                        : index *
                                          0.07,
                                    type: "spring",
                                    stiffness:
                                      330,
                                    damping: 21,
                                  }}
                                  style={{
                                    ...styles.socialAvatar,
                                    marginLeft:
                                      index ===
                                      0
                                        ? 0
                                        : -15,
                                    zIndex:
                                      socialProofPeople.length -
                                      index,
                                  }}
                                />
                              )
                            )}
                          </div>

                          <div
                            style={
                              styles.socialProofCopy
                            }
                          >
                            <span
                              style={
                                styles.socialProofStrong
                              }
                            >
                              {Math.max(
                                socialProofPeopleCount,
                                socialProofPeople.length
                              )}
                              + people
                            </span>{" "}
                            <span
                              style={
                                styles.socialProofLight
                              }
                            >
                              are waiting to
                              meet someone like
                              you.
                            </span>
                          </div>
                        </div>
                      )}

                      <motion.div
                        initial={{
                          opacity: 0,
                          scaleX: 0.25,
                        }}
                        animate={{
                          opacity: 1,
                          scaleX: 1,
                        }}
                        transition={{
                          delay:
                            reducedMotion
                              ? 0
                              : 0.25,
                          duration: 3.2,
                          ease: "linear",
                        }}
                        style={
                          styles.progressTrack
                        }
                      >
                        <motion.div
                          initial={{
                            scaleX: 0,
                          }}
                          animate={{
                            scaleX: 1,
                          }}
                          transition={{
                            duration:
                              reducedMotion
                                ? 0
                                : 3.8,
                            ease: "linear",
                          }}
                          style={
                            styles.progressFill
                          }
                        />
                      </motion.div>

                      <button
                        type="button"
                        onClick={
                          handleSkipToRecommendation
                        }
                        style={
                          styles.introSkipButton
                        }
                      >
                        Skip intro
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>

   {collapsed &&
  !showReservationSuccess && (
    <button
      type="button"
      onClick={handleReplayIntro}
      style={styles.replayButton}
      aria-label="Replay welcome"
      title="Replay welcome"
    >
      <IoRefreshOutline
        size={20}
        aria-hidden="true"
      />
    </button>
  )}
        </motion.div>

        <style>
          {`
            @keyframes tribeFirstReservationCursor {
              0%,
              45% {
                opacity: 1;
              }

              46%,
              100% {
                opacity: 0;
              }
            }
          `}
        </style>
      </motion.section>
    </AnimatePresence>
  );
}

const styles = {
  shell: {
    position: "fixed",
    zIndex: 9998,
    background: "#ffffff",
    overflow: "hidden",
    boxSizing: "border-box",
    fontFamily:
      "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    border:
      "1px solid rgba(15,23,42,0.08)",
  },

  shellExpanded: {
    border: "none",
  },

  shellCollapsed: {
    maxWidth: 720,
    margin: "0 auto",
    background: "#FFD84D",
    border:
      "1px solid rgba(11,11,11,0.1)",
  },

  shellSuccess: {
    maxWidth: 720,
    margin: "0 auto",
    background: "#ffffff",
    border: "1px solid #ddd",
  },

  topLogo: {
    position: "absolute",
    top:
      "calc(20px + env(safe-area-inset-top, 0px))",
    left: "40%",
    transform: "translateX(-50%)",
    zIndex: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },

  contentExpanded: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    transform: "none",
    padding:
      "calc(80px + env(safe-area-inset-top, 0px)) 24px calc(28px + env(safe-area-inset-bottom, 0px))",
    textAlign: "center",
  },

  topLogoSvg: {
    width: 94,
    height: "auto",
    display: "block",
  },

  content: {
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
  },

  contentSuccess: {
    display: "grid",
    gridTemplateColumns:
      "30px minmax(0, 1fr)",
    alignItems: "center",
    gap: 11,
    padding: "14px 16px",
    textAlign: "left",
  },

  successIconWrap: {
    width: 30,
    height: 30,
    display: "grid",
    placeItems: "center",
    alignSelf: "center",
    borderRadius: "50%",
    color: "#000",
    boxSizing: "border-box",
  },

  successCopy: {
    minWidth: 0,
    paddingRight: 2,
  },

  successTitle: {
    color: "#0b0b0b",
    fontSize: 16,
    lineHeight: 1.12,
    fontWeight: 850,
    letterSpacing: "-0.035em",
  },

  successSubtitle: {
    marginTop: 6,
    color: "rgba(11,11,11,0.64)",
    fontSize: 11.8,
    lineHeight: 1.34,
    fontWeight: 560,
    letterSpacing: "-0.014em",
  },

  successCountdownTrack: {
    width: "100%",
    height: 2,
    marginTop: 8,
    borderRadius: 999,
    background:
      "rgba(11,11,11,0.1)",
    overflow: "hidden",
  },

  successCountdownFill: {
    width: "100%",
    height: "100%",
    borderRadius: 999,
    background: "#22C55E",
    transformOrigin: "left center",
  },

  successInstructionStrong: {
    color: "#0b0b0b",
    fontWeight: 800,
  },

  successHighlight: {
    position: "relative",
    display: "inline-block",
    isolation: "isolate",
  },



  contentCollapsed: {
    display: "grid",
    gridTemplateColumns:
      "44px minmax(0, 1fr) 36px",
    alignItems: "center",
    gap: 0,
    padding:
      "11px 12px 11px 16px",
    textAlign: "left",
  },

  ticketIconWrap: {
    width: 0,
    height: 42,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    color: "#000",
    boxSizing: "border-box",
  },

  orb: {
    flexShrink: 0,
    position: "relative",
    display: "grid",
    placeItems: "center",
    background: "transparent",
    overflow: "hidden",
  },

  orbExpanded: {
    width: 275,
    height: 275,
    borderRadius: 0,
    marginBottom: 27,
    boxShadow: "none",
  },

  orbCollapsed: {
    width: 46,
    height: 46,
    borderRadius: 12,
    marginBottom: 0,
    boxShadow: "none",
  },

  reservationVideo: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "contain",
    objectPosition: "center",
    background: "transparent",
    pointerEvents: "none",
  },

  reservationVideoExpanded: {
    borderRadius: 0,
  },

  reservationVideoCollapsed: {
    borderRadius: 12,
  },

  expandedCopy: {
    width: "100%",
    maxWidth: 660,
  },

  eyebrow: {
    color: "#777",
    fontSize: 11,
    lineHeight: 1,
    fontWeight: 850,
    letterSpacing: "0.11em",
    marginBottom: 10,
  },

  headline: {
    width: "100%",
    maxWidth: 680,
    minHeight: "2.05em",
    margin: 0,
    color: "#292929",
    fontSize:
      "clamp(2.35rem, 8vw, 5rem)",
    lineHeight: 1,
    fontWeight: 520,
    letterSpacing: "-0.05em",
  },

  headlinePrefix: {
    color: "#292929",
    fontWeight: 320,
  },

  headlineEmphasis: {
    color: "#0b0b0b",
    fontWeight: 700,
  },

  highlightedWords: {
    position: "relative",
    whiteSpace: "nowrap",
  },

  cursor: {
    display: "inline-block",
    marginLeft: 2,
    fontWeight: 400,
    animation:
      "tribeFirstReservationCursor 0.8s steps(1) infinite",
  },

  supportingText: {
    maxWidth: 470,
    margin: "10px auto 0",
    color: "#656565",
    fontSize: 15,
    lineHeight: 1.45,
    fontWeight: 500,
    letterSpacing: "-0.022em",
  },

  supportingTextStrong: {
    color: "#2a2a2a",
    fontWeight: 760,
  },

  socialProofRow: {
    margin: "21px auto 0",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    maxWidth: "100%",
  },

  avatarStack: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    paddingLeft: 1,
  },

  socialAvatar: {
    width: 33,
    height: 33,
    display: "block",
    borderRadius: "50%",
    objectFit: "cover",
    background: "#ececec",
    border: "2px solid #ffffff",
    boxSizing: "border-box",
    boxShadow:
      "0 2px 7px rgba(15,23,42,0.14)",
  },

  socialProofCopy: {
    maxWidth: 245,
    color: "#646464",
    fontSize: 12.8,
    lineHeight: 1.25,
    letterSpacing: "-0.022em",
    textAlign: "left",
  },

  socialProofStrong: {
    color: "#151515",
    fontWeight: 850,
  },

  socialProofLight: {
    color: "#676767",
    fontWeight: 520,
  },

  progressTrack: {
    width: 52,
    height: 3,
    margin: "30px auto 0",
    borderRadius: 999,
    background:
      "rgba(17,17,17,0.1)",
    overflow: "hidden",
    transformOrigin: "center",
  },

  progressFill: {
    width: "100%",
    height: "100%",
    borderRadius: 999,
    background: "#111",
    transformOrigin: "left center",
  },




  recommendationCopy: {
    width: "100%",
    maxWidth: 560,
  },





  
contentRecommendation: {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  padding:
    "calc(24px + env(safe-area-inset-top, 0px)) 20px calc(28px + env(safe-area-inset-bottom, 0px))",
  textAlign: "center",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
},

  recommendationLogoWrap: {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 18,
  flexShrink: 0,
},

recommendationLogo: {
  width: 94,
  height: "auto",
  display: "block",
},



  introSkipButton: {
    display: "block",
    margin: "15px auto 0",
    padding: "10px 14px",
    border: "none",
    borderRadius: 11,
    background: "transparent",
    color: "#666",
    fontSize: 12.8,
    lineHeight: 1,
    fontWeight: 700,
    letterSpacing: "-0.015em",
    cursor: "pointer",
    WebkitTapHighlightColor:
      "transparent",
  },

  compactCopy: {
    minWidth: 0,
  },

  compactTitle: {
  color: "#0b0b0b",
  fontSize: 15,
  lineHeight: 1.15,
  fontWeight: 900,
  letterSpacing: "-0.04em",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  paddingBottom: 1,
},
  compactSubtitle: {
    marginTop: 5,
    color: "rgba(11,11,11,0.66)",
    fontSize: 11.8,
    lineHeight: 1.08,
    fontWeight: 620,
    letterSpacing: "-0.018em",
  },

  replayButton: {
    width: 36,
    height: 36,
    padding: 0,
    display: "grid",
    placeItems: "center",
    borderRadius: 10,
    border:
      "0px solid rgba(11,11,11,0.1)",
    background: "#FFD84D",
    color: "#0b0b0b",
    cursor: "pointer",
    WebkitTapHighlightColor:
      "transparent",
  },
};